import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { APP_NAME } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Wallet,
  Plus,
  Loader2,
  ArrowUpCircle,
  ArrowDownCircle,
  Settings2,
  History,
  ExternalLink,
  UserCircle,
  Key,
} from "lucide-react";

interface ClientWallet {
  id: string;
  client_id: string;
  balance: number;
  base_rate: number;
  updated_at: string;
  client_name?: string;
}

interface WalletTransaction {
  id: string;
  client_id: string;
  amount: number;
  type: string;
  description: string | null;
  tokens_used: number | null;
  conversation_id: string | null;
  created_by: string | null;
  key_source: string | null;
  created_at: string;
  // populated client-side
  conversation_title?: string;
  user_full_name?: string;
}

const Wallets = () => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [wallets, setWallets] = useState<ClientWallet[]>([]);
  const [loading, setLoading] = useState(true);

  // Add Credits dialog
  const [creditDialogOpen, setCreditDialogOpen] = useState(false);
  const [selectedWallet, setSelectedWallet] = useState<ClientWallet | null>(null);
  const [creditAmount, setCreditAmount] = useState("");
  const [creditDescription, setCreditDescription] = useState("");
  const [crediting, setCrediting] = useState(false);

  // Edit Base Rate dialog
  const [rateDialogOpen, setRateDialogOpen] = useState(false);
  const [rateWallet, setRateWallet] = useState<ClientWallet | null>(null);
  const [newRate, setNewRate] = useState("");
  const [savingRate, setSavingRate] = useState(false);

  // Transaction history dialog
  const [txDialogOpen, setTxDialogOpen] = useState(false);
  const [txWallet, setTxWallet] = useState<ClientWallet | null>(null);
  const [transactions, setTransactions] = useState<WalletTransaction[]>([]);
  const [txLoading, setTxLoading] = useState(false);
  const [txEmployees, setTxEmployees] = useState<{ id: string; full_name: string }[]>([]);
  const [txSelectedEmployee, setTxSelectedEmployee] = useState<string>("all");

  const fetchWallets = useCallback(async () => {
    setLoading(true);
    const { data: walletData, error } = await supabase
      .from("client_wallet")
      .select("*")
      .order("updated_at", { ascending: false });

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      setLoading(false);
      return;
    }

    // Fetch client names
    const clientIds = (walletData || []).map((w: any) => w.client_id);
    let clientMap: Record<string, string> = {};
    if (clientIds.length > 0) {
      const { data: clients } = await supabase
        .from("client")
        .select("id, name")
        .in("id", clientIds);
      if (clients) {
        clientMap = Object.fromEntries((clients as any[]).map((c) => [c.id, c.name]));
      }
    }

    setWallets(
      (walletData || []).map((w: any) => ({
        ...w,
        client_name: clientMap[w.client_id] ?? "Unknown",
      }))
    );
    setLoading(false);
  }, [toast]);

  useEffect(() => {
    fetchWallets();
  }, [fetchWallets]);

  // ── Add Credits ──────────────────────────────────────────────────────────

  const openCreditDialog = (wallet: ClientWallet) => {
    setSelectedWallet(wallet);
    setCreditAmount("");
    setCreditDescription("Credits added by super admin");
    setCreditDialogOpen(true);
  };

  const handleAddCredits = async () => {
    if (!selectedWallet) return;
    const amount = parseInt(creditAmount, 10);
    if (isNaN(amount) || amount <= 0) {
      toast({ title: "Invalid amount", description: "Enter a positive integer.", variant: "destructive" });
      return;
    }
    setCrediting(true);

    const { error: walletErr } = await supabase
      .from("client_wallet")
      .update({
        balance: selectedWallet.balance + amount,
        updated_at: new Date().toISOString(),
      })
      .eq("id", selectedWallet.id);

    if (walletErr) {
      toast({ title: "Error updating wallet", description: walletErr.message, variant: "destructive" });
      setCrediting(false);
      return;
    }

    const { data: { user } } = await supabase.auth.getUser();
    const { error: txErr } = await supabase.from("wallet_transactions").insert({
      client_id: selectedWallet.client_id,
      amount,
      type: "credit",
      description: creditDescription.trim() || "Credits added by super admin",
      created_by: user?.id ?? null,
    });

    if (txErr) {
      toast({ title: "Error recording transaction", description: txErr.message, variant: "destructive" });
    } else {
      toast({
        title: "Credits added",
        description: `${amount.toLocaleString()} points added to ${selectedWallet.client_name}.`,
      });
      setCreditDialogOpen(false);
      fetchWallets();
    }
    setCrediting(false);
  };

  // ── Edit Base Rate ───────────────────────────────────────────────────────

  const openRateDialog = (wallet: ClientWallet) => {
    setRateWallet(wallet);
    setNewRate(String(wallet.base_rate));
    setRateDialogOpen(true);
  };

  const handleSaveRate = async () => {
    if (!rateWallet) return;
    const rate = parseFloat(newRate);
    if (isNaN(rate) || rate < 0) {
      toast({ title: "Invalid rate", description: "Enter a non-negative number.", variant: "destructive" });
      return;
    }
    setSavingRate(true);
    const { error } = await supabase
      .from("client_wallet")
      .update({ base_rate: rate, updated_at: new Date().toISOString() })
      .eq("id", rateWallet.id);

    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } else {
      toast({
        title: "Base rate updated",
        description: `${rateWallet.client_name} rate set to ${rate} pts/1M tokens.`,
      });
      setRateDialogOpen(false);
      fetchWallets();
    }
    setSavingRate(false);
  };

  // ── Transaction History ──────────────────────────────────────────────────

  const fetchTransactions = useCallback(async (clientId: string, employeeId?: string) => {
    setTxLoading(true);

    let txQuery = supabase
      .from("wallet_transactions")
      .select("*")
      .eq("client_id", clientId)
      .order("created_at", { ascending: false })
      .limit(100);

    if (employeeId && employeeId !== "all") {
      txQuery = txQuery.eq("profile_id", employeeId);
    }

    const { data, error } = await txQuery;
    if (error) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
      setTxLoading(false);
      return;
    }

    const txList = (data as WalletTransaction[]) || [];

    // Resolve conversation titles + user_ids
    const convIds = [...new Set(txList.map((t) => t.conversation_id).filter(Boolean))] as string[];
    let convMap: Record<string, { title: string; user_id: string }> = {};
    if (convIds.length > 0) {
      const { data: convData } = await supabase
        .from("conversations")
        .select("id, title, user_id")
        .in("id", convIds);
      if (convData) {
        convMap = Object.fromEntries(
          (convData as { id: string; title: string; user_id: string }[]).map((c) => [
            c.id,
            { title: c.title, user_id: c.user_id },
          ])
        );
      }
    }

    // Batch-fetch profile full names
    const profileIds = [
      ...new Set([
        ...txList.map((t) => t.created_by).filter(Boolean),
        ...Object.values(convMap).map((c) => c.user_id).filter(Boolean),
      ]),
    ] as string[];

    let profileMap: Record<string, string> = {};
    if (profileIds.length > 0) {
      const { data: profileData } = await supabase
        .from("profiles")
        .select("id, full_name")
        .in("id", profileIds);
      if (profileData) {
        profileMap = Object.fromEntries(
          (profileData as { id: string; full_name: string | null }[]).map((p) => [
            p.id,
            p.full_name || p.id,
          ])
        );
      }
    }

    setTransactions(
      txList.map((tx) => {
        const conv = tx.conversation_id ? convMap[tx.conversation_id] : undefined;
        const userId = tx.type === "credit" ? tx.created_by : conv?.user_id ?? null;
        return {
          ...tx,
          conversation_title: conv?.title,
          user_full_name: userId ? profileMap[userId] : undefined,
        };
      })
    );
    setTxLoading(false);
  }, [toast]);

  const openTxDialog = async (wallet: ClientWallet) => {
    setTxWallet(wallet);
    setTransactions([]);
    setTxSelectedEmployee("all");
    setTxDialogOpen(true);

    // Fetch employees for this client
    const { data: empData } = await supabase
      .from("profiles")
      .select("id, full_name")
      .eq("client_id", wallet.client_id)
      .order("full_name");
    setTxEmployees(
      (empData as { id: string; full_name: string }[] || []).filter((e) => e.full_name)
    );

    fetchTransactions(wallet.client_id);
  };

  if (loading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-6 p-6 overflow-y-auto">
      <div>
        <h1 className="text-2xl font-semibold">Wallets</h1>
        <p className="text-sm text-muted-foreground">
          Manage client wallet balances and token deduction rates.
        </p>
      </div>

      {wallets.length === 0 ? (
        <Card className="flex flex-col items-center justify-center py-12">
          <Wallet className="h-12 w-12 text-muted-foreground mb-4" />
          <p className="text-sm text-muted-foreground">No wallets found. Create clients first.</p>
        </Card>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Client</TableHead>
                <TableHead className="text-right">Balance (pts)</TableHead>
                <TableHead className="text-right">Rate (pts/1M tokens)</TableHead>
                <TableHead>Last Updated</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {wallets.map((wallet) => (
                <TableRow key={wallet.id}>
                  <TableCell className="font-medium">{wallet.client_name}</TableCell>
                  <TableCell className="text-right font-mono">
                    <span className={wallet.balance < 0 ? "text-destructive" : ""}>
                      {wallet.balance.toLocaleString()}
                    </span>
                  </TableCell>
                  <TableCell className="text-right font-mono">{Number(wallet.base_rate).toLocaleString()}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {new Date(wallet.updated_at).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Add credits"
                      onClick={() => openCreditDialog(wallet)}
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Change base rate"
                      onClick={() => openRateDialog(wallet)}
                    >
                      <Settings2 className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      title="Transaction history"
                      onClick={() => openTxDialog(wallet)}
                    >
                      <History className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* ── Add Credits Dialog ─────────────────────────────────────────────── */}
      <Dialog open={creditDialogOpen} onOpenChange={setCreditDialogOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Add Credits</DialogTitle>
            <DialogDescription>
              Upload balance to <strong>{selectedWallet?.client_name}</strong>'s wallet.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="credit-amount">Amount (points) *</Label>
              <Input
                id="credit-amount"
                type="number"
                min={1}
                value={creditAmount}
                onChange={(e) => setCreditAmount(e.target.value)}
                placeholder="e.g. 10000"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="credit-desc">Description</Label>
              <Input
                id="credit-desc"
                value={creditDescription}
                onChange={(e) => setCreditDescription(e.target.value)}
                placeholder="Credits added by super admin"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddCredits} disabled={crediting}>
              {crediting && <Loader2 className="h-4 w-4 animate-spin" />}
              Add Credits
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Edit Base Rate Dialog ──────────────────────────────────────────── */}
      <Dialog open={rateDialogOpen} onOpenChange={setRateDialogOpen}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Change Base Rate</DialogTitle>
            <DialogDescription>
              Set the token deduction rate for <strong>{rateWallet?.client_name}</strong>.
              <br />
              <span className="text-xs text-muted-foreground">
                Default: 500 points per 1M tokens
              </span>
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="base-rate">Rate (points per 1M tokens) *</Label>
              <Input
                id="base-rate"
                type="number"
                min={0}
                step={0.01}
                value={newRate}
                onChange={(e) => setNewRate(e.target.value)}
                placeholder="500"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRateDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveRate} disabled={savingRate}>
              {savingRate && <Loader2 className="h-4 w-4 animate-spin" />}
              Save Rate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Transaction History Dialog ─────────────────────────────────────── */}
      <Dialog open={txDialogOpen} onOpenChange={setTxDialogOpen}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Transactions — {txWallet?.client_name}
            </DialogTitle>
            <DialogDescription>Last 100 transactions for this client.</DialogDescription>
          </DialogHeader>

          <div className="flex items-center gap-2">
            <UserCircle className="h-4 w-4 text-muted-foreground shrink-0" />
            <Select
              value={txSelectedEmployee}
              onValueChange={(value) => {
                setTxSelectedEmployee(value);
                if (txWallet) fetchTransactions(txWallet.client_id, value);
              }}
            >
              <SelectTrigger className="w-[220px]">
                <SelectValue placeholder="All Employees" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Employees</SelectItem>
                {txEmployees.map((emp) => (
                  <SelectItem key={emp.id} value={emp.id}>
                    {emp.full_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {txLoading ? (
            <div className="flex items-center justify-center py-10">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : transactions.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-10 text-center">
              <History className="h-10 w-10 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">No transactions yet.</p>
            </div>
          ) : (
            <div className="overflow-y-auto max-h-96">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>User</TableHead>
                    <TableHead>Conversation</TableHead>
                    <TableHead>Key Source</TableHead>
                    <TableHead className="text-right">Tokens</TableHead>
                    <TableHead className="text-right">Amount (pts)</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {transactions.map((tx) => (
                    <TableRow key={tx.id}>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                        {new Date(tx.created_at).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        {tx.type === "credit" ? (
                          <Badge variant="secondary" className="gap-1 text-green-700 bg-green-50">
                            <ArrowUpCircle className="h-3 w-3" />
                            Credit
                          </Badge>
                        ) : (
                          <Badge variant="secondary" className="gap-1 text-red-700 bg-red-50">
                            <ArrowDownCircle className="h-3 w-3" />
                            Debit
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        <span>{tx.description ?? "—"}</span>
                        {tx.conversation_title && (
                          <span className="text-muted-foreground"> — {tx.conversation_title}</span>
                        )}
                      </TableCell>
                      <TableCell className="text-sm">
                        {tx.user_full_name ?? <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell>
                        {tx.conversation_id ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 gap-1.5 px-2 text-xs font-mono text-muted-foreground hover:text-foreground"
                            title={`Open conversation: ${tx.conversation_title ?? tx.conversation_id}`}
                            onClick={() => {
                              setTxDialogOpen(false);
                              navigate(`/?conv=${tx.conversation_id}`);
                            }}
                          >
                            <ExternalLink className="h-3 w-3 shrink-0" />
                            <span className="max-w-[120px] truncate">
                              {tx.conversation_title ?? tx.conversation_id}
                            </span>
                          </Button>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {tx.key_source ? (
                          <Badge
                            variant="outline"
                            className={`gap-1 text-xs ${
                              tx.key_source === "client"
                                ? "text-blue-700 border-blue-200 bg-blue-50"
                                : "text-gray-600 border-gray-200 bg-gray-50"
                            }`}
                          >
                            <Key className="h-3 w-3" />
                            {tx.key_source === "client" ? "Client" : APP_NAME}
                          </Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-mono text-sm">
                        {tx.tokens_used != null ? tx.tokens_used.toLocaleString() : "—"}
                      </TableCell>
                      <TableCell
                        className={`text-right font-mono font-medium ${
                          tx.amount < 0 ? "text-destructive" : "text-green-700"
                        }`}
                      >
                        {tx.amount > 0 ? "+" : ""}
                        {tx.amount.toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setTxDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Wallets;
